import { useTranslation } from 'react-i18next';
import { useWatch, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { isEmailEdited } from '@/lib/user-utils';

/**
 * Sign-in address warning for member-edit forms: the email doubles as the
 * login identifier, so an admin editing it must know they are changing how
 * this person signs in. Returns the warning text for the email field's
 * description slot, or undefined outside edit mode / while unedited. The
 * watch is disabled without an original email (create mode), so those forms
 * skip the per-keystroke subscription entirely.
 */
export function useEmailChangeWarning<T extends FieldValues & { email: string }>(
  control: Control<T>,
  originalEmail: string | undefined
): string | undefined {
  const { t } = useTranslation();
  const emailValue = useWatch({
    control,
    name: 'email' as FieldPath<T>,
    disabled: !originalEmail
  }) as string | undefined;
  if (!originalEmail || !isEmailEdited(emailValue, originalEmail)) return undefined;
  return t('organization.members.emailChangeWarning');
}
